import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { StringDict } from '@azure/msal-common';
import { TranslocoService } from '@ngneat/transloco';
import { ModalService } from '@vinnova/ui';
import { AnalyticsEvent, AnalyticsTrackingService } from '@vinnova/utilities';
import {
  BehaviorSubject,
  Observable,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ChangeLangModalComponent } from '../components/change-lang-modal/change-lang-modal.component';
import { environment } from '../environment';
import { AuthCookieService } from './auth-cookie.service';

const analyticsLoginMethodKey = 'analytics_login_method';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private authCookieService = inject(AuthCookieService);
  private analyticsTrackingService = inject(AnalyticsTrackingService);
  private readonly injector = inject(Injector);
  private env = inject(environment);
  private loggedIn$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        take(1),
      )
      .subscribe(() => {
        const loggedIn =
          this.hasValidAccountInner() &&
          !!this.authCookieService.getCookie() &&
          this.getIdTokenClaimInner('sub') ===
            this.authCookieService.getB2CCookieValue('sub');
        this.loggedIn$.next(loggedIn);
      });
  }

  /**
   *
   * @returns An observable that emits if the logged in user is a bedomare once
   */
  public getIsBedomare(): Observable<boolean> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => {
        return (
          !!this.getIdTokenClaimInner('bedomareId') ||
          !!this.getIdTokenClaimInner('extension_bedomareId')
        );
      }),
    );
  }

  /**
   *
   * @returns An observable that emits the personDetails once
   */
  public getPersonDetails(): Observable<{
    email: string;
    firstName: string;
    lastName: string;
  }> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => {
        return {
          email: this.getIdTokenClaimInner('emails')
            ? this.getIdTokenClaimInner('emails')[0]
            : (this.getIdTokenClaimInner('email') as string),
          firstName: this.getIdTokenClaimInner('given_name') as string,
          lastName: this.getIdTokenClaimInner('family_name') as string,
        };
      }),
    );
  }

  public checkLoggedIn() {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        if (this.hasValidAccountInner()) {
          this.msalService.instance.setActiveAccount(
            this.getAccountForCurrentApp(),
          );
          this.authCookieService.addSite();

          //If the url is assign or registration route we dont want to call loginb2c at this point.
          if (this.isNotAssignFlow()) {
            this.loggedIn$.next(true);
          }
          return this.authCookieService.loginB2C().pipe(
            take(1),
            tap(() => {
              const method = sessionStorage.getItem(analyticsLoginMethodKey);
              sessionStorage.removeItem(analyticsLoginMethodKey);
              this.analyticsTrackingService.event(AnalyticsEvent.LOGIN, {
                method: method ?? 'unknown',
              });
              const storedReturnUrl = localStorage.getItem('returnUrl');
              if (storedReturnUrl) {
                const decodedUrl = decodeURIComponent(storedReturnUrl);
                localStorage.removeItem('returnUrl');
                if (decodedUrl.startsWith('http')) {
                  location.replace(decodedUrl);
                } else {
                  this.router.navigateByUrl(decodedUrl);
                }
              } else {
                this.router.navigateByUrl('/');
              }
            }),
          );
        } else {
          location.replace(`${this.env.etjansterCommonUrl}/public/login`);
        }
      }),
    );
  }

  public onLoggedIn(onLoggedIn?: Observable<unknown>[]) {
    return this.loggedIn$.pipe(
      filter((loggedIn) => loggedIn),
      take(1),
      switchMap(() => {
        if (onLoggedIn) {
          if (sessionStorage.getItem('lang')) {
            const modalService = this.injector.get(ModalService);
            modalService.show(ChangeLangModalComponent);
            sessionStorage.removeItem('lang');
          }

          return forkJoin(onLoggedIn);
        } else {
          return of(undefined);
        }
      }),
    );
  }

  private isNotAssignFlow() {
    const url = globalThis.location.href;
    const storedReturnUrl = localStorage.getItem('returnUrl') ?? '';

    return (
      !url.includes('assignbedomare') &&
      !url.includes('registration') &&
      !storedReturnUrl.includes('assignbedomare') &&
      !storedReturnUrl.includes('registration')
    );
  }

  public login(returnUrl: string, authority: string, scopes: string[]) {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        sessionStorage.setItem(
          analyticsLoginMethodKey,
          this.authCookieService.getCookie() ? 'cross_app_sso' : 'initial',
        );

        if (returnUrl && this.validateReturnUrl(returnUrl)) {
          localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));
        }

        return this.msalService.loginRedirect({
          authority,
          scopes,
          extraQueryParameters: {
            ui_locales: this.translocoService.getActiveLang(),
          } as StringDict,
        } as RedirectRequest);
      }),
    );
  }

  public edit(editPolicyAuthority) {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        return this.msalService.loginPopup({
          authority: editPolicyAuthority,
          extraQueryParameters: {
            ui_locales: this.translocoService.getActiveLang(),
          } as StringDict,
        } as RedirectRequest);
      }),
    );
  }

  public logout(redirectUrl?: string) {
    this.authCookieService.deleteSharedCookie();
    return redirectUrl
      ? this.msalService.logout({
          postLogoutRedirectUri: redirectUrl,
        })
      : this.msalService.logout();
  }

  /**
   *
   * @returns An observable that emits if the account is valid once
   */
  public hasValidAccount(): Observable<boolean> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => {
        return this.hasValidAccountInner();
      }),
    );
  }

  private hasValidAccountInner() {
    const accountForCurrentApp = this.getAccountForCurrentApp();
    if (!accountForCurrentApp) {
      return false;
    }

    const exp = accountForCurrentApp.idTokenClaims['exp'];
    return exp * 1000 > Date.now();
  }

  /**
   *
   * @returns An observable that emits once
   */
  public hasValidAccountAndActiveCookie(): Observable<boolean> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => {
        return this.hasValidAccountAndActiveCookieInner();
      }),
    );
  }

  private hasValidAccountAndActiveCookieInner(): boolean {
    return (
      this.authCookieService.getCookie() &&
      this.hasValidAccountInner() &&
      this.authCookieService.isCurrentSiteLoggedIn() &&
      this.authCookieService.getB2CCookieValue('sub') ===
        this.getIdTokenClaimInner('sub')
    );
  }

  /**
   *
   * @returns An observable that emits once
   */
  public getIdTokenClaim(claim: string, latestAccount?: boolean) {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => {
        return this.getIdTokenClaimInner(claim, latestAccount);
      }),
    );
  }

  private getIdTokenClaimInner(claim: string, latestAccount?: boolean) {
    const account = latestAccount
      ? this.getAccountForCurrentApp()
      : this.msalService.instance.getActiveAccount();
    return account?.idTokenClaims[claim];
  }

  public getAccessToken(
    scopes: string[],
    authority: string,
  ): Observable<AuthenticationResult> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() =>
        this.msalService.acquireTokenSilent({
          scopes,
          authority: authority + this.getIdTokenClaimInner('tfp'),
        }),
      ),
    );
  }

  private getAccountForCurrentApp() {
    return this.msalService.instance
      .getAllAccounts()
      .sort((a, b) =>
        a.idTokenClaims?.['exp'] > b.idTokenClaims?.['exp'] ? -1 : 1,
      )
      .find(
        (a) =>
          a.idTokenClaims &&
          a.idTokenClaims['aud'] ===
            this.msalService.instance.getConfiguration().auth.clientId,
      );
  }

  private validateReturnUrl(returnUrl: string) {
    const isAbsoluteUrl = returnUrl.startsWith('http');
    if (!isAbsoluteUrl) {
      return true;
    }

    try {
      const url = new URL(returnUrl);
      return url.hostname.endsWith('.vinnova.se');
    } catch {
      return false;
    }
  }
}
